import React, {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import Swal from 'sweetalert2'
import {KTIcon} from '../../../_payairo/helpers'
import {
  createAdminStaff,
  getAdminRoles,
  parseAdminApiErrorMessage,
  type AdminRole,
} from '../../modules/auth/core/_requests'
import { useAuth } from '../../modules/auth'

const ACCENT = '#064e3b' // Forest Green
const BORDER = '#d1fae5' // Light emerald border

const UserManagementRightToolbar = () => {
  const [input, setInput] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    roleId: '',
  })
  const [loading, setLoading] = useState(false)
  const [show, setShow] = useState(false)

  const { hasPermission } = useAuth()
  const canCreateStaff = hasPermission('admin.staff.create')

  const [roles, setRoles] = useState<AdminRole[]>([])
  const [rolesLoading, setRolesLoading] = useState(false)

  const loadRoles = async () => {
    setRolesLoading(true)
    try {
      const list = await getAdminRoles()
      setRoles(list)
    } catch (error) {
      console.error(error)
      setRoles([])
    } finally {
      setRolesLoading(false)
    }
  }

  useEffect(() => {
    if (show) {
      loadRoles()
    }
  }, [show])

  const resetForm = () => {
    setShow(false)
    setInput({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      roleId: '',
    })
  }

  const staffCreateErrorMessage = (err: unknown): string => {
    const ax = err as {response?: {data?: unknown}} | null
    if (ax?.response?.data && typeof ax.response.data === 'object') {
      const d = ax.response.data as Record<string, unknown>
      const detail = d.detail
      if (typeof detail === 'string' && detail.trim()) return detail
      if (Array.isArray(detail)) {
        const parts = detail
          .map((x) => {
            if (typeof x === 'string') return x
            if (x && typeof x === 'object' && 'msg' in x) {
              const m = (x as {msg?: string}).msg
              return typeof m === 'string' ? m : ''
            }
            return ''
          })
          .filter(Boolean)
        if (parts.length) return parts.join(' ')
      }
      const email = d.email
      if (Array.isArray(email) && email.length) return String(email[0])
      if (typeof email === 'string' && email.trim()) return email
      const password = d.password
      if (Array.isArray(password) && password.length) return String(password[0])
      if (typeof password === 'string' && password.trim()) return password
      return parseAdminApiErrorMessage(ax.response.data)
    }
    if (err instanceof Error && err.message) return err.message
    return 'Failed to add user.'
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !input.first_name.trim() ||
      !input.last_name.trim() ||
      !input.email.trim() ||
      !input.password
    ) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'First name, last name, email, and password are required.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
    }

    const nameRegex = /^[A-Za-z]+$/
    if (!nameRegex.test(input.first_name.trim())) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'First name should accept only alphabetical characters.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
    }

    if (!nameRegex.test(input.last_name.trim())) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'Last name should accept only alphabetical characters.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
    }

    if (
      input.password.length < 6 ||
      !/\d/.test(input.password) ||
      !/[^A-Za-z0-9]/.test(input.password)
    ) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'Password must be at least 6 characters, contain 1 numeric character, and 1 special character.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
    }

    if (!input.roleId.trim()) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'Please select a permission role.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
    }

    const role_id = Number.parseInt(input.roleId, 10)
    if (!Number.isFinite(role_id)) {
      return Swal.fire({
        titleText: 'Error!',
        text: 'Invalid role selection.',
        icon: 'error',
        confirmButtonText: 'Ok! Got It',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary px-6 rounded-3',
        },
      })
    }

    const body = {
      email: input.email.trim(),
      password: input.password,
      first_name: input.first_name.trim(),
      last_name: input.last_name.trim(),
      role_id,
    }

    setLoading(true)
    try {
      const created = await createAdminStaff(body)
      setLoading(false)
      await Swal.fire({
        titleText: 'Success!',
        text: `Staff user created (${created.username} · ${created.role.display_name}).`,
        icon: 'success',
        confirmButtonText: 'Great!',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-success px-6 rounded-3',
        },
      })
      window.dispatchEvent(new CustomEvent('REFRESH_USERS_LIST'))
      resetForm() // Also close the modal and reset form
    } catch (err) {
      setLoading(false)
      console.error(err)
      await Swal.fire({
        titleText: 'Error!',
        text: staffCreateErrorMessage(err),
        icon: 'error',
        confirmButtonText: 'Try Again',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger px-6 rounded-3',
        },
      })
    }
  }

  const onChangeHandler = (e: any) => {
    setInput((prev) => ({...prev, [e.target.name]: e.target.value}))
  }

  return (
    <>
      {canCreateStaff && (
        <button
          className='btn btn-primary fw-bolder px-8 rounded-3 shadow-sm transition-all hover-elevated-sm'
          style={{backgroundColor: ACCENT, borderColor: ACCENT}}
          onClick={() => {
            setShow(true)
          }}
        >
          <i className='fas fa-user-plus me-2 fs-6' />
          Add New User
        </button>
      )}

      <Modal
        show={show}
        onHide={resetForm}
        centered
        size='lg'
        className='modal-add-user'
        contentClassName='rounded-4 border-0 shadow-lg overflow-hidden'
      >
        <div className='modal-content border-0'>
          <div className='modal-header border-0 pb-2 pt-8 px-8 bg-white d-flex align-items-center justify-content-between'>
            <div>
              <h2 className='fw-bolder fs-1 mb-1' style={{color: ACCENT, letterSpacing: '-0.02em'}}>
                Add New User
              </h2>
              <p className='text-gray-500 fs-7 mb-0 fw-medium'>
                Create a new account and assign a permission role
              </p>
            </div>
            <button
              type='button'
              className='btn btn-icon btn-sm btn-light-emerald rounded-circle shadow-sm'
              style={{backgroundColor: BORDER}}
              onClick={resetForm}
            >
              <span style={{color: ACCENT}}>
                <KTIcon iconName='cross' className='fs-2' />
              </span>
            </button>
          </div>

          <div className='modal-body px-8 pt-6 pb-2 scroll-y' style={{maxHeight: '70vh'}}>
            <form id='kt_modal_add_user_form' className='form' onSubmit={onSubmit}>
              <div className='mb-12'>
                <div className='d-flex align-items-center gap-3 mb-8'>
                  <div className='h-2px w-30px rounded' style={{backgroundColor: ACCENT}} />
                  <h3
                    className='fw-bold fs-4 mb-0 text-uppercase'
                    style={{color: ACCENT, letterSpacing: '0.05em'}}
                  >
                    Personal Information
                  </h3>
                </div>

                <div className='row g-6'>
                  <div className='col-md-6'>
                    <div className='p-6 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm'>
                      <label
                        className='required text-gray-700 fw-bolder text-uppercase mb-3 d-block'
                        style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                      >
                        First name
                      </label>
                      <input
                        type='text'
                        name='first_name'
                        required
                        autoComplete='given-name'
                        value={input.first_name}
                        onChange={onChangeHandler}
                        className='form-control form-control-solid rounded-3 border-0 py-3 bg-white shadow-sm fw-bold fs-6'
                        placeholder='Staff'
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='p-6 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm'>
                      <label
                        className='required text-gray-700 fw-bolder text-uppercase mb-3 d-block'
                        style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                      >
                        Last name
                      </label>
                      <input
                        type='text'
                        name='last_name'
                        required
                        autoComplete='family-name'
                        value={input.last_name}
                        onChange={onChangeHandler}
                        className='form-control form-control-solid rounded-3 border-0 py-3 bg-white shadow-sm fw-bold fs-6'
                        placeholder='User'
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='p-6 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm'>
                      <label
                        className='required text-gray-700 fw-bolder text-uppercase mb-3 d-block'
                        style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                      >
                        Email address
                      </label>
                      <input
                        type='email'
                        name='email'
                        required
                        autoComplete='email'
                        value={input.email}
                        onChange={onChangeHandler}
                        className='form-control form-control-solid rounded-3 border-0 py-3 bg-white shadow-sm fw-bold fs-6'
                        placeholder='staff@example.com'
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='p-6 rounded-4 bg-light bg-opacity-30 border border-gray-100 shadow-sm'>
                      <label
                        className='required text-gray-700 fw-bolder text-uppercase mb-3 d-block'
                        style={{fontSize: '0.65rem', letterSpacing: '0.1em'}}
                      >
                        Password
                      </label>
                      <input
                        type='password'
                        name='password'
                        required
                        autoComplete='new-password'
                        value={input.password}
                        onChange={onChangeHandler}
                        className='form-control form-control-solid rounded-3 border-0 py-3 bg-white shadow-sm fw-bold fs-6'
                        placeholder='••••••••'
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='mb-8'>
                <div className='d-flex align-items-center gap-3 mb-8'>
                  <div className='h-2px w-30px rounded' style={{backgroundColor: ACCENT}} />
                  <h3
                    className='fw-bold fs-4 mb-0 text-uppercase'
                    style={{color: ACCENT, letterSpacing: '0.05em'}}
                  >
                    Assign Permission Role
                  </h3>
                </div>

                <div className='row g-4'>
                  {rolesLoading ? (
                    <div className='col-12 py-10 text-center text-gray-400 fw-bold'>
                      <span className='spinner-border spinner-border-sm me-2' role='status' />
                      Loading available roles...
                    </div>
                  ) : roles.length === 0 ? (
                    <div className='col-12 py-10 text-center text-gray-500 fw-semibold fs-7'>
                      No roles could be loaded. Check your connection or permissions, then try
                      opening the dialog again.
                    </div>
                  ) : (
                    roles.map((role) => (
                      <div className='col-md-6' key={role.id}>
                        <label
                          className={`d-flex align-items-center p-5 rounded-4 border transition-all cursor-pointer h-100 ${
                            input.roleId === String(role.id)
                              ? 'border-emerald border-opacity-50 bg-light-emerald'
                              : 'border-gray-100 bg-white shadow-sm'
                          }`}
                          style={{
                            backgroundColor: input.roleId === String(role.id) ? BORDER : undefined,
                            borderColor: input.roleId === String(role.id) ? ACCENT : undefined,
                          }}
                        >
                          <div className='form-check form-check-custom form-check-solid me-5'>
                            <input
                              className='form-check-input h-20px w-20px'
                              name='roleId'
                              type='radio'
                              required
                              value={String(role.id)}
                              checked={input.roleId === String(role.id)}
                              onChange={onChangeHandler}
                              style={{
                                border:
                                  input.roleId === String(role.id)
                                    ? `2px solid ${ACCENT}`
                                    : undefined,
                              }}
                            />
                          </div>
                          <div className='min-w-0'>
                            <div className='fw-bolder fs-6 mb-1 text-gray-900 lh-tight'>
                              {role.display_name}
                            </div>
                            <div className='text-gray-500 fs-8 fw-medium text-truncate-2'>
                              {role.description?.trim()
                                ? role.description
                                : 'No description provided.'}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className='modal-footer border-0 bg-white pt-6 px-8 pb-8 d-flex justify-content-between align-items-center gap-3 shadow-top-sm'>
            <button
              type='button'
              className='btn btn-light fw-bolder px-8 rounded-3 transition-all'
              onClick={resetForm}
            >
              Discard
            </button>
            <button
              type='submit'
              form='kt_modal_add_user_form'
              className='btn btn-primary px-12 rounded-3 fw-bolder shadow-sm d-flex align-items-center'
              style={{backgroundColor: ACCENT, borderColor: ACCENT}}
              disabled={loading}
            >
              {!loading ? (
                <>
                  <i className='fas fa-check me-2 fs-7' />
                  Create User
                </>
              ) : (
                <>
                  <span className='spinner-border spinner-border-sm me-3' role='status' />
                  Processing...
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        .text-truncate-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .hover-elevated-sm:hover {
           transform: translateY(-1px);
           box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
        }
        .shadow-top-sm {
           box-shadow: 0 -1px 3px rgba(0,0,0,0.02);
        }
      `}</style>
    </>
  )
}

export default UserManagementRightToolbar
